import React, { Component } from "react"
import { loadStripe } from "@stripe/stripe-js"
import { Sparkles, Lock } from "lucide-react"
import posthog from "src/consent/posthog"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ApiHandler from "src/api/ApiHandler"
import "./StripeCheckout.css"


interface StripeCheckoutProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
    isLoggedIn: boolean
    onComplete: (paymentIntentId: string) => void
    onCancel: () => void
    onLoginRedirect: () => void
}

interface StripeCheckoutState {
    loading: boolean
    processing: boolean
    error: string | null
    alreadyLifetime: boolean
    amountCents: number | null
    paymentIntentId: string | null
}


class StripeCheckout extends Component<StripeCheckoutProps, StripeCheckoutState> {
    private cardRef: React.RefObject<HTMLDivElement>
    private stripe: any = null
    private elements: any = null
    private paymentElement: any = null
    private mounted = false
    // Funnel instrumentation flags: distinguish "left before trying" from "tried and failed".
    private reachedForm = false
    private attemptedPayment = false

    constructor(props: StripeCheckoutProps) {
        super(props)
        this.state = {
            loading: true,
            processing: false,
            error: null,
            alreadyLifetime: false,
            amountCents: null,
            paymentIntentId: null,
        }
        this.cardRef = React.createRef()
    }

    async componentDidMount() {
        if (this.mounted) return
        this.mounted = true
        await this.initializePayment()
    }

    componentWillUnmount() {
        // If the user saw the payment form but never clicked pay, they abandoned before
        // trying. (Clicking pay sets attemptedPayment, so 3DS redirects don't count here.)
        if (this.reachedForm && !this.attemptedPayment) {
            posthog?.capture("checkout_abandoned", {
                payment_intent_id: this.state.paymentIntentId,
                had_error: !!this.state.error,
            })
        }
        if (this.paymentElement) this.paymentElement.unmount()
    }

    async initializePayment() {
        try {
            const response = await this.props.apiHandler.createCheckout()
            if (!response?.client_secret || !response?.public_key) {
                posthog?.capture("checkout_init_failed", { reason: "missing_client_secret" })
                this.setState({ loading: false, error: "Failed to create payment session" })
                return
            }
            this.setState({
                amountCents: response.amount ?? null,
                paymentIntentId: response.payment_intent_id ?? null,
            })

            this.stripe = await loadStripe(response.public_key)
            if (!this.stripe) {
                this.setState({ loading: false, error: "Failed to load payment provider" })
                return
            }

            this.elements = this.stripe.elements({
                clientSecret: response.client_secret,
                appearance: {
                    theme: "stripe",
                    variables: {
                        colorPrimary: "#004d3f",
                        colorText: "#14201c",
                        colorTextSecondary: "#5f6b66",
                        borderRadius: "12px",
                        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                        fontSizeBase: "15px",
                        spacingUnit: "3px",
                    },
                    rules: {
                        ".AccordionItem": {
                            border: "1px solid #dce8e3",
                            boxShadow: "none",
                            padding: "14px 16px",
                        },
                        ".AccordionItem:hover": {
                            backgroundColor: "#f6faf8",
                        },
                        ".AccordionItem--selected": {
                            border: "1.5px solid #0b5a45",
                            backgroundColor: "#eaf6f1",
                        },
                    },
                },
            })

            this.paymentElement = this.elements.create("payment", {
                paymentMethodOrder: ["apple_pay", "google_pay", "card", "revolut_pay"],
            })

            if (this.cardRef.current) {
                this.paymentElement.mount(this.cardRef.current)
            }

            this.reachedForm = true
            posthog?.capture("checkout_form_shown", {
                payment_intent_id: this.state.paymentIntentId,
                amount_cents: this.state.amountCents,
            })
            this.setState({ loading: false })
        } catch (error: any) {
            const message = error?.message || error?.error || "Something went wrong. Please try again."
            const isAlreadyLifetime = typeof message === "string" && message.toLowerCase().includes("you already have")
            console.error("Stripe setup error:", message)
            if (!isAlreadyLifetime) {
                posthog?.capture("checkout_init_failed", { reason: "exception", message })
            }
            this.setState({ loading: false, error: message, alreadyLifetime: isAlreadyLifetime })
        }
    }

    handleSubmit = async () => {
        if (!this.stripe || !this.elements || this.state.processing) return

        this.attemptedPayment = true
        posthog?.capture("pay_button_clicked", { payment_intent_id: this.state.paymentIntentId })
        this.setState({ processing: true, error: null })

        const returnUrl = new URL(`${window.location.origin}/plans`)
        if (this.state.paymentIntentId) {
            returnUrl.searchParams.set("pi", this.state.paymentIntentId)
        }

        const { error } = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl.toString(),
            },
            redirect: "if_required",
        })

        if (error) {
            posthog?.capture("payment_error", {
                payment_intent_id: this.state.paymentIntentId,
                message: error.message,
                code: error.code,
                decline_code: error.decline_code,
                type: error.type,
            })
            this.setState({ processing: false, error: error.message })
        } else {
            this.setState({ processing: false })
            this.props.onComplete(this.state.paymentIntentId!)
        }
    }

    render() {
        return (
            <div className="stripe-checkout-container">
                {this.state.loading && (
                    <div className="stripe-checkout-loading">Loading payment...</div>
                )}
                {this.state.alreadyLifetime && (
                    <div className="stripe-checkout-lifetime-active">
                        <p className="stripe-checkout-lifetime-title">Lifetime Access Active</p>
                        <p className="stripe-checkout-lifetime-subtitle">You have full access to every feature, forever.</p>
                    </div>
                )}
                {this.state.error && !this.state.alreadyLifetime && (
                    <div className="stripe-checkout-error-message">
                        {this.state.error}
                    </div>
                )}
                {!this.state.loading && !this.state.alreadyLifetime && (
                    <p className="stripe-payment-label">Choose payment method</p>
                )}
                <div className="stripe-payment-element-wrap">
                    <div ref={this.cardRef as React.RefObject<HTMLDivElement>} />
                    {!this.state.loading && !this.state.alreadyLifetime && (
                        <div className="stripe-card-brands" aria-hidden>
                            <img src="/payment/visa.svg" alt="Visa" />
                            <img src="/payment/mastercard.svg" alt="Mastercard" />
                        </div>
                    )}
                </div>
                {!this.state.loading && !this.state.alreadyLifetime && (
                    <div className="stripe-checkout-actions">
                        <button
                            className="stripe-checkout-pay"
                            onClick={this.handleSubmit}
                            disabled={this.state.processing}
                        >
                            {this.state.processing ? (
                                <span className="pay-strong">Processing...</span>
                            ) : (
                                <>
                                    <Lock size={16} strokeWidth={2.4} aria-hidden />
                                    <span className="pay-label">
                                        <span className="pay-strong">{this.state.amountCents ? `Pay €${(this.state.amountCents / 100).toFixed(0)} once` : "Unlock"}</span>
                                        <span className="pay-dot" aria-hidden>·</span>
                                        <span className="pay-light">Unlock forever</span>
                                    </span>
                                </>
                            )}
                        </button>
                        <p className="stripe-trust-line" aria-label="Payment security">
                            <Lock size={12} strokeWidth={2} aria-hidden className="stripe-trust-line-ic" />
                            Secure payment powered by
                            <img className="stripe-inline-logo" src="/payment/stripe.svg" alt="Stripe" />
                            <span className="stripe-trust-sep" aria-hidden>·</span>
                            30-day money-back guarantee
                        </p>
                    </div>
                )}
            </div>
        )
    }
}

export default StripeCheckout
